const HOLD_CEILING_MS = 5_000;

let pendingGrants = 0;
let idleWaiters: Array<() => void> = [];
let listenerRegistered = false;

function notifyIdleWaiters() {
  if (pendingGrants !== 0) {
    return;
  }

  const waiters = idleWaiters;
  idleWaiters = [];
  for (const resolve of waiters) {
    resolve();
  }
}

function waitUntilIdle(): Promise<void> {
  if (pendingGrants === 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    idleWaiters.push(resolve);
  });
}

/**
 * Await in-flight token grants, but never longer than the ceiling — a hung request must not wedge reloads.
 */
export async function waitForPendingTokenGrants(
  ceilingMs: number = HOLD_CEILING_MS,
): Promise<void> {
  if (pendingGrants === 0) {
    return;
  }

  await Promise.race([
    waitUntilIdle(),
    new Promise<void>((resolve) => {
      setTimeout(resolve, ceilingMs);
    }),
  ]);
}

function registerHmrReloadHold() {
  if (listenerRegistered || !import.meta.hot) {
    return;
  }

  listenerRegistered = true;
  import.meta.hot.on("vite:beforeFullReload", async () => {
    await waitForPendingTokenGrants();
  });
}

/**
 * Hold Vite's full reload while an OAuth token grant is in flight so a mid-flight HMR reload
 * cannot orphan a rotated refresh token.
 */
export async function holdWhileTokenGrant<T>(work: () => Promise<T>): Promise<T> {
  registerHmrReloadHold();
  pendingGrants += 1;
  try {
    return await work();
  } finally {
    pendingGrants -= 1;
    notifyIdleWaiters();
  }
}

/** Test-only: reset module state between cases. */
export function __resetDevHmrReloadHoldForTests() {
  pendingGrants = 0;
  idleWaiters = [];
  listenerRegistered = false;
}

/** Test-only: pending grant count. */
export function __getPendingTokenGrantCountForTests() {
  return pendingGrants;
}
